import { queryOptions } from "@tanstack/react-query";
import {
  copyExercise,
  createExercise,
  deleteExercise,
  getExerciseById,
  listExercises,
  removeExerciseImage,
  updateExercise,
  uploadExerciseImage as uploadExerciseImageRequest,
  uploadExerciseVideo as uploadExerciseVideoRequest,
} from "api-client";
import { getMuscleNamesByGroups } from "utils";

import {
  type CreateExerciseInput,
  type ExerciseList,
  type ExerciseDetail,
  type ExerciseListInput,
  type ExercisesRepository,
  type UpdateExerciseInput,
} from "./ExercisesRepository";

export function createPersonalLibraryExercisesRepository() {
  const rootKey = ["exercises", "personal-library"] as const;

  async function list(input: ExerciseListInput) {
    const muscleNames = getMuscleNamesByGroups(input.muscleGroup ?? []);
    const response = await listExercises({
      query: {
        pageIndex: input.pageIndex,
        pageSize: input.pageSize,
        query: input.q,
        showOwned: input.showOwned?.toString(),
        showGlobal: input.showGlobal?.toString(),
        muscles:
          input.muscles ?? (muscleNames.length > 0 ? muscleNames : undefined),
        categories: input.category,
        movementPatterns: input.movementPattern,
      },
    });
    return response.data.data;
  }

  const repository = {
    rootKey,
    list,
    listOptions(input: ExerciseListInput) {
      return queryOptions<ExerciseList>({
        queryKey: [...rootKey, "list", input],
        queryFn: () => list(input),
      });
    },
    detailOptions(exerciseId: string) {
      return queryOptions<ExerciseDetail>({
        queryKey: [...rootKey, "detail", exerciseId],
        async queryFn() {
          const response = await getExerciseById({ path: { exerciseId } });
          return response.data.data;
        },
      });
    },
    async createExercise(input: CreateExerciseInput) {
      const response = await createExercise({ body: input });
      return response.data.data;
    },
    async copyExercise(exerciseId: string) {
      const response = await copyExercise({ path: { exerciseId } });
      return response.data.data;
    },
    deleteExercise(exerciseId: string) {
      return deleteExercise({ path: { exerciseId } });
    },
    async updateExercise(input: UpdateExerciseInput) {
      const { id, ...body } = input;
      return updateExercise({ path: { exerciseId: id }, body });
    },
    async uploadExerciseImage(exerciseId: string, file: File) {
      const response = await uploadExerciseImageRequest({
        path: { exerciseId },
        body: { file },
      });
      return response.data.data;
    },
    async uploadExerciseVideo(exerciseId: string, file: File) {
      const response = await uploadExerciseVideoRequest({
        path: { exerciseId },
        body: { file },
      });
      return response.data.data;
    },
    async removeExerciseImage(exerciseId: string, imageId: string) {
      return removeExerciseImage({ path: { exerciseId, imageId } });
    },
  };

  return repository satisfies ExercisesRepository;
}
